// HFLedger/AccountManager.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AccountManager.css'; // 필요시 CSS 파일 생성

function AccountManager({ user, language, SERVER_ROOT, settings }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  
  // 폼 데이터
  const [formData, setFormData] = useState({
    account_name: '',
    account_type: 'cash',
    currency: settings.default_currency,
    initial_balance: 0
  });
  
  // 데이터 불러오기
  useEffect(() => {
    if (!user) return;
    
    const fetchAccounts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${SERVER_ROOT}/api/hfl/accounts/${user.uid}`);
        setAccounts(response.data);
      } catch (err) {
        console.error('Error fetching accounts:', err);
        setError(err.message || '계좌 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAccounts();
  }, [user, SERVER_ROOT]);

  // 편집 시 폼 데이터 업데이트
  useEffect(() => {
    if (editingAccount) {
      setFormData({
        account_name: editingAccount.account_name,
        account_type: editingAccount.account_type,
        currency: editingAccount.currency,
        initial_balance: editingAccount.initial_balance
      });
    } else {
      setFormData({
        account_name: '',
        account_type: 'cash',
        currency: settings.default_currency,
        initial_balance: 0
      });
    }
  }, [editingAccount, settings.default_currency]);

  // 입력 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'initial_balance' ? parseFloat(value) || 0 : value
    }));
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 필수 필드 검증
    if (!formData.account_name.trim()) {
      setError(language === 'KR' 
        ? '계좌 이름을 입력해주세요.' 
        : 'Please enter an account name.');
      return;
    }
    
    try {
      if (editingAccount) {
        // 계좌 업데이트
        await axios.put(`${SERVER_ROOT}/api/hfl/accounts/${editingAccount.account_id}`, {
          account_name: formData.account_name,
          account_type: formData.account_type
        });
        
        // 목록 업데이트
        const updatedAccount = {
          ...editingAccount,
          account_name: formData.account_name,
          account_type: formData.account_type
        };
        
        setAccounts(prev => 
          prev.map(acc => acc.account_id === editingAccount.account_id ? updatedAccount : acc)
        );
      } else {
        // 새 계좌 생성
        const response = await axios.post(`${SERVER_ROOT}/api/hfl/accounts`, {
          user_id: user.uid,
          account_name: formData.account_name,
          account_type: formData.account_type,
          currency: formData.currency,
          initial_balance: formData.initial_balance
        });
        
        // 목록에 추가
        const newAccount = response.data;
        setAccounts(prev => [...prev, {
          account_id: newAccount.account_id,
          account_name: newAccount.account_name,
          account_type: newAccount.account_type,
          currency: newAccount.currency,
          initial_balance: newAccount.initial_balance,
          current_balance: newAccount.current_balance
        }]);
      }
      
      // 폼 초기화
      setShowForm(false);
      setEditingAccount(null);
      setFormData({
        account_name: '',
        account_type: 'cash',
        currency: settings.default_currency,
        initial_balance: 0
      });
      setError(null);
      
    } catch (err) {
      console.error('Error saving account:', err);
      setError(err.message || (language === 'KR' 
        ? '계좌를 저장하는 중 오류가 발생했습니다.' 
        : 'Error saving account.'));
    }
  };

  // 계좌 삭제 핸들러
  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm(language === 'KR' 
      ? '이 계좌를 삭제하시겠습니까? 이 계좌의 거래 내역은 남아있게 됩니다.' 
      : 'Are you sure you want to delete this account? Transactions linked to this account will remain.')) {
      return;
    }
    
    try {
      await axios.delete(`${SERVER_ROOT}/api/hfl/accounts/${accountId}`);
      
      // 목록에서 제거
      setAccounts(prev => prev.filter(acc => acc.account_id !== accountId));
      
    } catch (err) {
      console.error('Error deleting account:', err);
      alert(language === 'KR' ? '계좌 삭제 중 오류가 발생했습니다.' : 'Error deleting account.');
    }
  };

  // 계좌 편집 핸들러
  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setShowForm(true);
    setError(null);
  };

  // 통화 포맷팅 헬퍼 함수
  const formatCurrency = (amount, currency = settings.default_currency) => {
    return new Intl.NumberFormat(language === 'KR' ? 'ko-KR' : 'en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading && accounts.length === 0) {
    return (
      <div className="accounts-loading">
        <div className="accounts-spinner"></div>
        <p>{language === 'KR' ? '계좌 정보 로딩 중...' : 'Loading accounts...'}</p>
      </div>
    );
  }

  return (
    <div className="account-manager-container">
      <div className="account-manager-header" style={{display: 'flex', justifyContent: 'left'}}>
        <button 
          className="add-account-button"
          onClick={() => {
            setEditingAccount(null);
            setShowForm(true);
            setError(null);
          }} 
        >
          <i className="fas fa-plus"></i> 
          {language === 'KR' ? '새 계좌 추가' : 'Add New Account'}
        </button>
      </div>
      
      {/* 계좌 추가/편집 폼 */}
      {showForm && (
        <div className="account-form-container">
          <h4>
            {editingAccount 
              ? (language === 'KR' ? '계좌 수정' : 'Edit Account') 
              : (language === 'KR' ? '새 계좌 추가' : 'Add New Account')}
          </h4>
          
          <form onSubmit={handleSubmit} className="account-form">
            {/* 계좌 이름 */}
            <div className="form-group">
              <label htmlFor="account_name">
                {language === 'KR' ? '계좌 이름' : 'Account Name'} <span className="required">*</span>
              </label>
              <input 
                type="text" 
                id="account_name"
                name="account_name"
                value={formData.account_name}
                onChange={handleInputChange}
                placeholder={language === 'KR' ? '계좌 이름 입력' : 'Enter account name'}
                required
              />
            </div>
            
            {/* 계좌 유형 */}
            <div className="form-group">
              <label htmlFor="account_type">{language === 'KR' ? '계좌 유형' : 'Account Type'}</label>
              <select 
                id="account_type"
                name="account_type"
                value={formData.account_type}
                onChange={handleInputChange}
              >
                <option value="cash">{language === 'KR' ? '현금' : 'Cash'}</option>
                <option value="checking">{language === 'KR' ? '입출금계좌' : 'Checking'}</option>
                <option value="savings">{language === 'KR' ? '저축계좌' : 'Savings'}</option>
                <option value="credit">{language === 'KR' ? '신용카드' : 'Credit Card'}</option>
                <option value="investment">{language === 'KR' ? '투자계좌' : 'Investment'}</option>
                <option value="other">{language === 'KR' ? '기타' : 'Other'}</option>
              </select>
            </div>
            
            {/* 새 계좌 추가시에만 표시할 필드들 */}
            {!editingAccount && (
              <>
                {/* 통화 */}
                <div className="form-group">
                  <label htmlFor="currency">{language === 'KR' ? '통화' : 'Currency'}</label>
                  <select 
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                  >
                    <option value="KRW">KRW (₩)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="JPY">JPY (¥)</option>
                    <option value="CNY">CNY (¥)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
                
                {/* 초기 잔액 */}
                <div className="form-group">
                  <label htmlFor="initial_balance">{language === 'KR' ? '초기 잔액' : 'Initial Balance'}</label>
                  <input 
                    type="number" 
                    id="initial_balance"
                    name="initial_balance"
                    value={formData.initial_balance}
                    onChange={handleInputChange}
                    step="0.01"
                  />
                </div>
              </>
            )}
            
            {/* 에러 메시지 */}
            {error && (
              <div className="form-error">
                {error}
              </div>
            )}
            
            {/* 제출 버튼 */}
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => {
                  setShowForm(false);
                  setError(null);
                }}
              >
                {language === 'KR' ? '취소' : 'Cancel'}
              </button>
              <button 
                type="submit" 
                className="submit-button"
              >
                {editingAccount 
                  ? (language === 'KR' ? '수정' : 'Update') 
                  : (language === 'KR' ? '추가' : 'Add')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* 계좌 목록 */}
      <div className="account-list">
        {accounts.length === 0 ? (
          <p className="no-accounts">
            {language === 'KR' ? '등록된 계좌가 없습니다.' : 'No accounts found.'}
          </p>
        ) : (
          accounts.map(account => (
            <div key={account.account_id} className="account-item">
              <div className="account-info">
                <h4 className="account-name">{account.account_name}</h4>
                <span className="account-type">{account.account_type}</span>
              </div>
              <div className="account-balance">
                {formatCurrency(account.current_balance, account.currency)}
              </div>
              <div className="account-actions">
                <button 
                  className="edit-button"
                  onClick={() => handleEditAccount(account)}
                  title={language === 'KR' ? '수정' : 'Edit'}
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button 
                  className="delete-button"
                  onClick={() => handleDeleteAccount(account.account_id)}
                  title={language === 'KR' ? '삭제' : 'Delete'}
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* 도움말 */}
      <div className="account-help">
        <p>
          <i className="fas fa-info-circle"></i>
          {language === 'KR' 
            ? '거래 내역을 추가하려면 최소 하나 이상의 계좌가 필요합니다.' 
            : 'You need at least one account to add transactions.'}
        </p>
      </div>
    </div>
  );
}

export default AccountManager;